import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, FileSignature, Printer, MessageCircle, CheckCircle2, XCircle, Ban,
  AlertTriangle, Loader2, RotateCcw, X, Wrench, Package, Plus, Trash2, Search, Save, Archive, ArchiveRestore
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate } from '@/lib/api'
import { navegarLista } from '@/lib/keyboard'

interface Item {
  id: number; produto_id: number; codigo?: string; descricao: string
  quantidade: number; preco_unit: number; desconto: number; total: number
  eh_servico: boolean
}
interface Orcamento {
  id: number; numero: number; tipo: 'venda' | 'servico'; status: string
  cliente_id: number; cliente_nome: string | null; cliente_telefone: string | null
  vendedor_nome: string | null; tecnico_nome: string | null
  vendedor_cad_id?: number | null; tecnico_cad_id?: number | null
  equipamento_id?: number | null
  descricao_problema: string | null; observacao: string | null
  valido_ate: string; expirado: boolean; dias_restantes: number
  subtotal: number; desconto: number; total: number
  arquivado?: boolean
  convertido_pedido_id: number | null; convertido_os_id: number | null
  criado_em: string; itens: Item[]
}
interface Divergencia {
  item_id: number; produto_id: number; descricao: string
  tipo: 'estoque' | 'preco'
  quantidade_orcada?: number; estoque_atual?: number; falta?: number
  preco_orcado?: number; preco_atual?: number; diferenca?: number
}

export default function OrcamentoDetalhePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [conferencia, setConferencia] = useState<any>(null)
  const [editando, setEditando] = useState(false)
  const [itensEdit, setItensEdit] = useState<any[]>([])
  const [buscaProd, setBuscaProd] = useState('')
  const [abrirBusca, setAbrirBusca] = useState(false)
  const [hiProd, setHiProd] = useState(0)

  const { data: o, isLoading, refetch } = useQuery<Orcamento>({
    queryKey: ['orcamento', id],
    queryFn: async () => (await api.get(`/orcamentos/${id}`)).data,
  })

  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['orc-edit-produtos', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', {
      params: { q: buscaProd.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrirBusca && buscaProd.trim().length > 0,
  })

  const recarregar = () => {
    refetch()
    qc.invalidateQueries({ queryKey: ['orcamentos'] })
  }

  // window.open() abre a URL crua, SEM o header de autenticação — o backend
  // responde 401. Busca via api (que injeta o token) e abre o blob local.
  const [gerandoPdf, setGerandoPdf] = useState<string | null>(null)
  const imprimir = async (formato: 'a4' | 'cupom') => {
    setGerandoPdf(formato)
    try {
      const r = await api.get(`/orcamentos/${id}/pdf`, {
        params: { formato }, responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      const w = window.open(url, '_blank')
      if (!w) toast.error('Libere os pop-ups para abrir o PDF')
      // Revoga depois de um tempo: revogar na hora quebra a aba recém-aberta.
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      toast.error('Não foi possível gerar o PDF')
    } finally {
      setGerandoPdf(null)
    }
  }

  const enviarZap = useMutation({
    mutationFn: async () => {
      const tel = window.prompt('WhatsApp do cliente:', o?.cliente_telefone || '')
      if (tel === null) throw new Error('cancelado')
      return (await api.post(`/orcamentos/${id}/enviar-whatsapp`,
        { telefone: tel.trim() || undefined, formato: 'a4' })).data
    },
    onSuccess: (r: any) => toast.success(`Orçamento enviado para ${r.enviado_para}`),
    onError: (e: any) => {
      if (e?.message === 'cancelado') return
      toast.error(e?.response?.data?.detail || 'Erro ao enviar')
    },
  })

  const recusar = useMutation({
    mutationFn: async () => (await api.post(`/orcamentos/${id}/recusar`)).data,
    onSuccess: () => { toast.success('Orçamento marcado como recusado'); recarregar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const cancelar = useMutation({
    mutationFn: async () => (await api.post(`/orcamentos/${id}/cancelar`)).data,
    onSuccess: () => { toast.success('Orçamento cancelado'); recarregar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const renovar = useMutation({
    mutationFn: async () => (await api.post(`/orcamentos/${id}/renovar`, null, { params: { dias: 7 } })).data,
    onSuccess: (r: any) => { toast.success(`Validade estendida até ${formatDate(r.valido_ate)}`); recarregar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  // Abre a conferência antes de converter — nunca converte às cegas.
  const conferir = useMutation({
    mutationFn: async () => (await api.get(`/orcamentos/${id}/conferir`)).data,
    onSuccess: (r) => setConferencia(r),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao conferir'),
  })

    // PUT substitui os itens inteiros (o backend apaga e regrava) — por isso
  // mandamos a lista completa, não só o que mudou.
  const salvarItens = useMutation({
    mutationFn: async () => (await api.put(`/orcamentos/${id}`, {
      tipo: o!.tipo,
      cliente_id: o!.cliente_id,
      vendedor_cad_id: (o as any)!.vendedor_cad_id ?? null,
      tecnico_cad_id: (o as any)!.tecnico_cad_id ?? null,
      equipamento_id: o!.equipamento_id ?? null,
      descricao_problema: o!.descricao_problema,
      observacao: o!.observacao,
      valido_ate: o!.valido_ate,
      desconto: o!.desconto,
      itens: itensEdit.map(i => ({
        produto_id: i.produto_id, quantidade: i.quantidade,
        preco_unit: i.preco_unit, desconto: i.desconto || 0,
      })),
    })).data,
    onSuccess: () => { toast.success('Itens atualizados'); setEditando(false); recarregar() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const converter = useMutation({
    mutationFn: async () => (await api.post(`/orcamentos/${id}/converter`)).data,
    onSuccess: (r: any) => {
      setConferencia(null)
      if (r.tipo === 'servico') {
        toast.success(`OS #${r.os_numero} criada`)
        navigate(`/servicos/${r.os_id}`)
      } else {
        // Venda não vira pedido aqui: o PDV monta o carrinho e cobra.
        sessionStorage.setItem('orcamento_para_pdv', JSON.stringify(r))
        toast.success('Carrinho montado no PDV — finalize o pagamento')
        navigate('/pdv')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao converter'),
  })

  const arquivar = useMutation({
    mutationFn: async () => (await api.post(
      `/orcamentos/${id}/${o?.arquivado ? 'desarquivar' : 'arquivar'}`)).data,
    onSuccess: (r: any) => {
      toast.success(r.arquivado ? 'Orçamento arquivado' : 'Orçamento restaurado')
      recarregar()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  if (isLoading || !o) return <div className="p-6">Carregando...</div>

  const aberto = o.status === 'aberto'
  const servicos = o.itens.filter(i => i.eh_servico)
  const pecas = o.itens.filter(i => !i.eh_servico)

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/orcamentos')} className="btn-secondary !px-2">
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSignature className="text-brand-600" /> Orçamento #{o.numero}
        </h1>
        <span className={`text-xs px-2 py-0.5 rounded ${
          o.tipo === 'servico' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
          {o.tipo === 'servico' ? 'Serviço' : 'Venda'}
        </span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => imprimir('a4')} disabled={!!gerandoPdf} className="btn-secondary text-sm">
            {gerandoPdf === 'a4' ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} A4
          </button>
          <button onClick={() => imprimir('cupom')} disabled={!!gerandoPdf} className="btn-secondary text-sm">
            {gerandoPdf === 'cupom' ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Cupom
          </button>
          <button onClick={() => enviarZap.mutate()} disabled={enviarZap.isPending}
            className="btn-secondary text-sm text-emerald-700">
            {enviarZap.isPending ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
            WhatsApp
          </button>
        </div>
      </div>

      {o.status === 'aceito' && (
        <div className="card card-body bg-emerald-50 border-emerald-200 text-sm text-emerald-900 flex items-center gap-2">
          <CheckCircle2 size={18} />
          Aceito e convertido.
          {o.convertido_os_id && (
            <Link to={`/servicos/${o.convertido_os_id}`} className="underline font-semibold">
              Abrir OS #{o.convertido_os_id}
            </Link>
          )}
        </div>
      )}
      {aberto && o.expirado && (
        <div className="card card-body bg-amber-50 border-amber-200 flex items-center gap-3">
          <AlertTriangle className="text-amber-600 shrink-0" size={20} />
          <div className="flex-1 text-sm text-amber-900">
            Este orçamento venceu em <strong>{formatDate(o.valido_ate)}</strong>.
            Renove a validade para poder converter — os preços serão reconferidos.
          </div>
          <button onClick={() => renovar.mutate()} disabled={renovar.isPending}
            className="btn-secondary !py-1 text-xs">
            <RotateCcw size={12} /> Renovar por 7 dias
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Campo rotulo="Cliente" valor={o.cliente_nome || '—'} />
        <Campo rotulo="Validade" valor={formatDate(o.valido_ate)}
          nota={aberto && !o.expirado ? `${o.dias_restantes} dia(s) restante(s)` : undefined} />
        <Campo rotulo={o.tipo === 'servico' ? 'Mecânico' : 'Vendedor'}
          valor={(o.tipo === 'servico' ? o.tecnico_nome : o.vendedor_nome) || '—'} />
        <Campo rotulo="Total" valor={formatMoney(o.total)} destaque />
      </div>

      {o.descricao_problema && (
        <div className="card p-3">
          <div className="text-xs text-ink-500 uppercase mb-1">Serviço solicitado</div>
          <div className="text-sm">{o.descricao_problema}</div>
        </div>
      )}

            <div className="card">
        {aberto && (
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-sm font-medium">Itens</span>
            {editando ? (
              <div className="flex gap-2">
                <button onClick={() => setEditando(false)} className="btn-secondary !py-1 text-xs">
                  Cancelar
                </button>
                <button onClick={() => salvarItens.mutate()} disabled={salvarItens.isPending || itensEdit.length === 0}
                  className="btn-primary !py-1 text-xs">
                  {salvarItens.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Salvar itens
                </button>
              </div>
            ) : (
              <button onClick={() => { setItensEdit(o.itens.map(i => ({ ...i }))); setEditando(true) }}
                className="btn-secondary !py-1 text-xs">
                <Plus size={12} /> Adicionar / editar itens
              </button>
            )}
          </div>
        )}

        {editando && (
          <div className="p-3 border-b relative">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
              <input value={buscaProd} autoComplete="off"
                onChange={e => { setBuscaProd(e.target.value); setAbrirBusca(true); setHiProd(0) }}
                onFocus={() => setAbrirBusca(true)}
                onKeyDown={navegarLista(produtos, hiProd, setHiProd,
                  (p: any) => {
                    setItensEdit(prev => {
                      const i = prev.findIndex(x => x.produto_id === p.id)
                      if (i >= 0) {
                        const novo = [...prev]
                        novo[i] = { ...novo[i], quantidade: novo[i].quantidade + 1 }
                        return novo
                      }
                      return [...prev, {
                        id: `novo-${p.id}`, produto_id: p.id, codigo: p.codigo, descricao: p.descricao,
                        quantidade: 1, preco_unit: Number(p.preco_venda || 0), desconto: 0,
                        eh_servico: (p.tipo || '') === 'servico',
                      }]
                    })
                    setBuscaProd(''); setAbrirBusca(false)
                  },
                  () => setAbrirBusca(false))}
                className="input w-full pl-8 text-sm" placeholder="Buscar produto ou serviço para adicionar..." />
            </div>
            {abrirBusca && buscaProd.trim() && (
              <div className="absolute z-20 mt-1 left-3 right-3 max-h-56 overflow-auto bg-white border border-ink-200 rounded-lg shadow-lg">
                {produtos.length === 0 ? (
                  <div className="p-3 text-center text-sm text-ink-500">Nenhum produto encontrado</div>
                ) : produtos.map((p: any, i: number) => (
                  <button key={p.id} onMouseEnter={() => setHiProd(i)}
                    onClick={() => {
                      setItensEdit(prev => {
                        const i = prev.findIndex(x => x.produto_id === p.id)
                        if (i >= 0) {
                          const novo = [...prev]
                          novo[i] = { ...novo[i], quantidade: novo[i].quantidade + 1 }
                          return novo
                        }
                        return [...prev, {
                          id: `novo-${p.id}`, produto_id: p.id, codigo: p.codigo, descricao: p.descricao,
                          quantidade: 1, preco_unit: Number(p.preco_venda || 0), desconto: 0,
                          eh_servico: (p.tipo || '') === 'servico',
                        }]
                      })
                      setBuscaProd(''); setAbrirBusca(false)
                    }}
                    className={`w-full px-3 py-2 text-left border-b last:border-0 flex items-center justify-between gap-2 ${
                      i === hiProd ? 'bg-brand-100' : 'hover:bg-brand-50'}`}>
                    <span className="text-sm truncate" title={p.descricao}>{p.descricao}</span>
                    <span className="text-sm font-semibold text-emerald-700 shrink-0">
                      {formatMoney(Number(p.preco_venda || 0))}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Tipo</th><th>Código</th><th>Descrição</th>
              <th className="text-right">Qtd</th><th className="text-right">Preço</th>
              <th className="text-right">Total</th>
              {editando && <th className="w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {(editando ? itensEdit : [...servicos, ...pecas]).map((i: any, idx: number) => (
              <tr key={i.id} className="border-b">
                <td>
                  <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                    i.eh_servico ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
                    {i.eh_servico ? <><Wrench size={10} className="inline" /> Serviço</>
                                  : <><Package size={10} className="inline" /> Peça</>}
                  </span>
                </td>
                <td className="font-mono text-xs text-ink-500 whitespace-nowrap">{i.codigo || '—'}</td>
                <td title={i.descricao}>{i.descricao}</td>
                <td className="text-right">
                  {editando ? (
                    <input type="number" min="0" step="0.001" value={i.quantidade}
                      onChange={e => setItensEdit(prev => prev.map((x, k) =>
                        k === idx ? { ...x, quantidade: parseFloat(e.target.value) || 0 } : x))}
                      className="input !py-1 text-sm text-right w-20" />
                  ) : i.quantidade}
                </td>
                <td className="text-right">
                  {editando ? (
                    <input type="number" min="0" step="0.01" value={i.preco_unit}
                      onChange={e => setItensEdit(prev => prev.map((x, k) =>
                        k === idx ? { ...x, preco_unit: parseFloat(e.target.value) || 0 } : x))}
                      className="input !py-1 text-sm text-right w-28" />
                  ) : <span className="font-mono">{formatMoney(i.preco_unit)}</span>}
                </td>
                <td className="text-right font-mono font-semibold">
                  {formatMoney(editando ? (i.preco_unit * i.quantidade - (i.desconto || 0)) : i.total)}
                </td>
                {editando && (
                  <td className="text-right">
                    <button onClick={() => setItensEdit(prev => prev.filter((_, k) => k !== idx))}
                      className="text-rose-600 hover:bg-rose-50 rounded p-1"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="text-right py-2 px-3 text-ink-600">Subtotal</td>
              <td className="text-right py-2 px-3 font-mono">
                {formatMoney(editando
                  ? itensEdit.reduce((s, i) => s + (i.preco_unit * i.quantidade - (i.desconto || 0)), 0)
                  : o.subtotal)}
              </td>
              {editando && <td></td>}
            </tr>
            {o.desconto > 0 && (
              <tr>
                <td colSpan={4} className="text-right py-1 px-3 text-ink-600">Desconto</td>
                <td className="text-right py-1 px-3 font-mono text-rose-600">- {formatMoney(o.desconto)}</td>
                {editando && <td></td>}
              </tr>
            )}
            <tr className="border-t">
              <td colSpan={4} className="text-right py-2 px-3 font-bold">Total</td>
              <td className="text-right py-2 px-3 font-mono font-bold text-lg">
                {formatMoney(editando
                  ? itensEdit.reduce((s, i) => s + (i.preco_unit * i.quantidade - (i.desconto || 0)), 0) - o.desconto
                  : o.total)}
              </td>
              {editando && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>

      {aberto && (
        <div className="flex items-center gap-2">
          <button onClick={() => conferir.mutate()} disabled={conferir.isPending}
            className="btn-primary">
            {conferir.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Cliente aceitou — converter em {o.tipo === 'servico' ? 'OS' : 'venda'}
          </button>
          <button onClick={() => recusar.mutate()} className="btn-secondary text-amber-700">
            <XCircle size={14} /> Cliente recusou
          </button>
          <button onClick={() => arquivar.mutate()} disabled={arquivar.isPending}
            className="btn-secondary ml-auto">
            {o.arquivado ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            {o.arquivado ? 'Restaurar' : 'Arquivar'}
          </button>
          <button onClick={() => { if (confirm('Cancelar este orçamento?')) cancelar.mutate() }}
            className="btn-secondary text-rose-700">
            <Ban size={14} /> Cancelar
          </button>
        </div>
      )}

            {/* Arquivar também vale pra recusado/cancelado/aceito — são justamente os
          que poluem a lista. O bloco de ações acima só aparece com o orçamento
          aberto, então estes precisam do botão aqui. */}
      {!aberto && (
        <div className="flex">
          <button onClick={() => arquivar.mutate()} disabled={arquivar.isPending}
            className="btn-secondary ml-auto">
            {arquivar.isPending ? <Loader2 size={14} className="animate-spin" />
              : o.arquivado ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            {o.arquivado ? 'Restaurar da lista' : 'Arquivar'}
          </button>
        </div>
      )}

      {conferencia && (
        <ModalConferencia
          conf={conferencia} tipo={o.tipo}
          onFechar={() => setConferencia(null)}
          onConverter={() => converter.mutate()}
          convertendo={converter.isPending}
          onRenovar={() => { renovar.mutate(); setConferencia(null) }}
        />
      )}
    </div>
  )
}

function Campo({ rotulo, valor, nota, destaque }: {
  rotulo: string; valor: string; nota?: string; destaque?: boolean
}) {
  return (
    <div className="card p-3">
      <div className="text-xs text-ink-500 uppercase">{rotulo}</div>
      <div className={destaque ? 'text-xl font-bold' : 'font-medium truncate'}>{valor}</div>
      {nota && <div className="text-xs text-ink-500">{nota}</div>}
    </div>
  )
}

/** Mostra o que mudou desde o orçamento. Não bloqueia por estoque — avisa,
 *  porque o sistema permite venda com saldo negativo em qualquer outra tela. */
function ModalConferencia({ conf, tipo, onFechar, onConverter, convertendo, onRenovar }: {
  conf: { pode_converter: boolean; vencido: boolean; motivo_bloqueio: string | null; divergencias: Divergencia[] }
  tipo: string
  onFechar: () => void; onConverter: () => void; convertendo: boolean; onRenovar: () => void
}) {
  const estoque = conf.divergencias.filter(d => d.tipo === 'estoque')
  const preco = conf.divergencias.filter(d => d.tipo === 'preco')
  const limpo = conf.divergencias.length === 0

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onFechar}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Confirmar conversão</h3>
          <button onClick={onFechar}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3 text-sm">
          {!conf.pode_converter && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-rose-900">
              {conf.motivo_bloqueio}
            </div>
          )}

          {limpo && conf.pode_converter && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-900 flex items-center gap-2">
              <CheckCircle2 size={16} />
              Tudo confere — nada mudou desde que o orçamento foi feito.
            </div>
          )}

          {estoque.length > 0 && (
            <div>
              <div className="font-semibold text-amber-800 mb-1">Estoque insuficiente nesta loja</div>
              <div className="space-y-1">
                {estoque.map(d => (
                  <div key={`e${d.item_id}`} className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
                    <div className="font-medium">{d.descricao}</div>
                    <div className="text-xs text-amber-800">
                      Orçado {d.quantidade_orcada} · disponível {d.estoque_atual} · faltam <strong>{d.falta}</strong>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink-500 mt-1">
                A conversão não fica bloqueada por isso — mas confira antes de entregar ao cliente.
              </p>
            </div>
          )}

          {preco.length > 0 && (
            <div>
              <div className="font-semibold text-sky-800 mb-1">Preço mudou desde o orçamento</div>
              <div className="space-y-1">
                {preco.map(d => (
                  <div key={`p${d.item_id}`} className="rounded border border-sky-200 bg-sky-50 px-3 py-2">
                    <div className="font-medium">{d.descricao}</div>
                    <div className="text-xs text-sky-800">
                      Orçado {formatMoney(d.preco_orcado!)} · hoje {formatMoney(d.preco_atual!)}
                      {' '}({d.diferenca! > 0 ? '+' : ''}{formatMoney(d.diferenca!)})
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink-500 mt-1">
                O valor orçado será mantido — foi o preço prometido ao cliente.
              </p>
            </div>
          )}
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onFechar} className="btn-secondary">Voltar</button>
          {conf.vencido ? (
            <button onClick={onRenovar} className="btn-primary">
              <RotateCcw size={14} /> Renovar validade
            </button>
          ) : (
            <button onClick={onConverter} disabled={!conf.pode_converter || convertendo}
              className="btn-primary">
              {convertendo ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {tipo === 'servico' ? 'Criar OS' : 'Abrir no PDV'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}